/**
 * slashCommands.ts — parse a user input string into a server cmd.
 *
 * The legacy SWF defined 115 "/" commands. Most are sugar over four
 * server cmds:
 *   - `sound_mess` (soundboard triggers — 53 arnold clips + 14 tass +
 *     1 takamura + 1 walrus, plus aliases)
 *   - `pm` / `say` (typed text)
 *   - `chat_cmd` (broadcast a generic "<user> did /xyz" system line)
 *   - the admin-only Easter eggs (`bomb`, `walrus`, `bucket`)
 *
 * A handful are CLIENT-ONLY actions (`/help`, `/cls`, `/ignore`,
 * `/profile`, `/me`). Those return a `{ kind: 'client', op: ... }`
 * shape that the chat hook handles locally.
 *
 * This file is pure (no React, no DOM). Unit-tested in ../test/slashCommands.test.ts.
 */

export type Parsed =
    | { kind: 'say'; m: string }
    | { kind: 'pm'; to: string; m: string }
    | { kind: 'sound'; p: string }
    | { kind: 'cmd'; c: string }
    | { kind: 'server'; cmd: 'bomb' | 'walrus' | 'bucket' }
    | { kind: 'styled'; m: string; style: 'rotate' | 'invert' | 'bbc' | 'body' }
    | { kind: 'c4_invite'; to: string }
    | { kind: 'c4_stats'; user?: string }
    | { kind: 'buzz'; to: string }
    | { kind: 'client'; op:
        | { t: 'help' }
        | { t: 'clear' }
        | { t: 'ignore'; user: string }
        | { t: 'unignore'; user: string }
        | { t: 'profile'; user: string }
        | { t: 'me'; text: string }
        | { t: 'room'; room: string }
        | { t: 'reply'; text: string }
        | { t: 'soundboards' }
        | { t: 'classes' }
        | { t: 'login' }
        | { t: 'list' }
        | { t: 'whois'; user: string }
        | { t: 'version' }
        | { t: 'time' }
        | { t: 'noop' }
      }
    | { kind: 'unknown'; c: string };

/** Soundboard map (slash → 'board/clip', no extension; matches what the server resolves). */
export const SOUNDS: Record<string, string> = {
    // arnold board (53 clips on disk; one cmd per file basename)
    an_arrest:           'arnold/an_arrest',
    cant_answer:         'arnold/cant_answer',
    cant_kill:           'arnold/cant_kill',
    cookies:             'arnold/cookies',
    eat_my_cookies:      'arnold/eat_my_cookies',
    dont_do_dat:         'arnold/dont_do_dat',
    dont_do_that:        'arnold/dont_do_that',
    erased:              'arnold/erased',
    few_more_questions:  'arnold/few_more_questions',
    get_out:             'arnold/get_out',
    get_your_mother:     'arnold/get_your_mother',
    gonna_ask_questions: 'arnold/gonna_ask_questions',
    good_bye:            'arnold/good_bye',
    good_morning:        'arnold/good_morning',
    hahaha:              'arnold/hahaha',
    hasta:               'arnold/hasta',
    hey:                 'arnold/hey',
    how_are_you:         'arnold/how_are_you',
    i_hear_you:          'arnold/i_hear_you',
    im_a_cop:            'arnold/im_a_cop',
    im_a_police:         'arnold/im_a_police',
    im_detective:        'arnold/im_detective',
    lied:                'arnold/lied',
    mission:             'arnold/mission',
    negativ:             'arnold/negativ',
    nice_to_meet_you:    'arnold/nice_to_meet_you',
    no:                  'arnold/no',
    no_deal:             'arnold/no_deal',
    no_problemo:         'arnold/no_problemo',
    no_shit:             'arnold/no_shit',
    postponed:           'arnold/postponed',
    programmed:          'arnold/programmed',
    put_cookie_down:     'arnold/put_cookie_down',
    quiet:               'arnold/quiet',
    sorry:               'arnold/sorry',
    stop_it:             'arnold/stop_it',
    stop_shouting:       'arnold/stop_shouting',
    stop_whining:        'arnold/stop_whining',
    talk_to_the_hand:    'arnold/talk_to_the_hand',
    talk_to_your_mother: 'arnold/talk_to_your_mother',
    terminated:          'arnold/terminated',
    turbo_time:          'arnold/turbo_time',
    what_do_you_want:    'arnold/what_do_you_want',
    who_are_you:         'arnold/who_are_you',
    who_is_your_daddy:   'arnold/who_is_your_daddy',
    why:                 'arnold/why',
    why_not:             'arnold/why_not',
    will_not_kill:       'arnold/will_not_kill',
    yea:                 'arnold/yea',
    yes:                 'arnold/yes',
    you_idiot:           'arnold/you_idiot',
    you_lack_discipline: 'arnold/you_lack_discipline',
    // takamura board
    takamura:            'takamura/takamura',
    // tass board (the slash cmds are short; we accept Tass-prefixed too).
    // Note: /play_connect4 is the *invite* command, handled separately
    // (see Parsed['c4_invite']) — the sound clip itself plays locally on
    // incoming invite, not as a standalone trigger.
    weaintplayin_short:  'tass/weaintplayin_short',
    // walrus board (note: `/walrus` is also an admin Easter-egg server cmd;
    // see precedence below — admin sends `walrus` server cmd; non-admin
    // triggers the walrus/scream soundboard).
    scream:              'walrus/scream',
};

/** Aliases for short-form commands the legacy SWF exposed. */
const ALIAS: Record<string, string> = {
    bye:        'good_bye',
    gm:         'good_morning',
    morning:    'good_morning',
    haha:       'hahaha',
    laugh:      'hahaha',
    hi:         'hey',
    arnold:     'terminated',
    cop:        'im_a_cop',
    police:     'im_a_police',
    detective:  'im_detective',
    'la_vista': 'hasta',
    sb:         'soundboards',          // resolves to client 'soundboards'
    pwd:        'pword',                // legacy
    pword:      'pword',                // self
};

/** Slash commands that ALWAYS go through the server's `chat_cmd` (broadcast a system line). */
const CHAT_CMDS = new Set([
    'data', 'obj', 'var', 'vars',
    'b8', 'e_', 'fu', 'max', 'prn', 'qgs', 'ra', 'rs', 'sm', 'u4', 'uu', 'uyl',
    'v7', 'w6', 'x5', 'x7', 'xb', 'xp', 'z_', 'za', 'embed', 'eviladminpanel',
    'uploads', 'widgets', 'www', 'vbz', 'avatar_imgembedded', 'default',
    'txt', // text-only mode: just send the body (handled as a styled cmd)
]);

/** Text-style commands — transform the message body, send as a styled say. */
const STYLE_CMDS = new Set(['rotate', 'invert', 'bbc', 'body']);

/** Client-only commands. */
const CLIENT_CMDS = new Set([
    'help', 'cls', 'clear', 'ignore', 'unignore',
    'me', 'profile', 'room', 'reply', 'soundboards', 'login',
    'nick', 'name', 'classes',
    'list', 'users', 'who', 'whois', 'version', 'ver', 'time', 'uptime',
]);

/**
 * Parse a single line of user input. Lines NOT starting with `/` are
 * plain say messages.
 */
export function parse(input: string, isAdmin = false): Parsed {
    const text = input.trim();
    if (!text) return { kind: 'client', op: { t: 'noop' } };
    if (text[0] !== '/') return { kind: 'say', m: text };

    // tokenize
    const m = /^\/([A-Za-z][A-Za-z0-9_]*)(\s+(.*))?$/.exec(text);
    if (!m) return { kind: 'cmd', c: text.slice(1) };
    let head = m[1]!.toLowerCase();
    const rest = (m[3] ?? '').trim();

    // alias resolution
    if (ALIAS[head]) head = ALIAS[head]!;

    // PM-family
    if (head === 'pm' || head === 'msg' || head === 'whisper' || head === 'w') {
        const sp = rest.indexOf(' ');
        if (sp <= 0) return { kind: 'client', op: { t: 'help' } };
        return { kind: 'pm', to: rest.slice(0, sp), m: rest.slice(sp + 1) };
    }
    if (head === 'reply' || head === 'r') return { kind: 'client', op: { t: 'reply', text: rest } };

    // /buzz <user>  — send the legacy ping_user "buzz" sound notification
    if (head === 'buzz' || head === 'ping') {
        if (!rest.trim()) return { kind: 'client', op: { t: 'help' } };
        return { kind: 'buzz', to: rest.trim() };
    }

    // Client-only
    if (CLIENT_CMDS.has(head)) {
        switch (head) {
            case 'help':     return { kind: 'client', op: { t: 'help' } };
            case 'cls':
            case 'clear':    return { kind: 'client', op: { t: 'clear' } };
            case 'ignore':   return rest ? { kind: 'client', op: { t: 'ignore',   user: rest } } : { kind: 'client', op: { t: 'help' } };
            case 'unignore': return rest ? { kind: 'client', op: { t: 'unignore', user: rest } } : { kind: 'client', op: { t: 'help' } };
            case 'profile':  return rest ? { kind: 'client', op: { t: 'profile',  user: rest } } : { kind: 'client', op: { t: 'help' } };
            case 'me':       return { kind: 'client', op: { t: 'me',    text: rest } };
            case 'room':     return rest ? { kind: 'client', op: { t: 'room',    room: rest } } : { kind: 'client', op: { t: 'help' } };
            case 'soundboards': return { kind: 'client', op: { t: 'soundboards' } };
            case 'classes': return { kind: 'client', op: { t: 'classes' } };
            case 'login':    return { kind: 'client', op: { t: 'login' } };
            case 'list':
            case 'users':    return { kind: 'client', op: { t: 'list' } };
            case 'who':
            case 'whois':    return rest ? { kind: 'client', op: { t: 'whois', user: rest } } : { kind: 'client', op: { t: 'list' } };
            case 'version':
            case 'ver':      return { kind: 'client', op: { t: 'version' } };
            case 'time':
            case 'uptime':   return { kind: 'client', op: { t: 'time' } };
            case 'nick':
            case 'name':
                // vB owns names — surface as a help nudge
                return { kind: 'client', op: { t: 'help' } };
        }
    }

    // Admin Easter eggs (server gates the rank check)
    if (head === 'bomb' || head === 'walrus' || head === 'bucket') {
        // For admins, send the server cmd. For non-admins, fall through to
        // either the walrus soundboard or chat_cmd announcement.
        if (isAdmin) return { kind: 'server', cmd: head as 'bomb' | 'walrus' | 'bucket' };
        if (head === 'walrus') return { kind: 'sound', p: 'walrus/scream' };
        return { kind: 'cmd', c: head };
    }

    // Soundboard?
    if (SOUNDS[head]) return { kind: 'sound', p: SOUNDS[head]! };

    // Connect 4 — `/play_connect4 <user>` invites them to play
    if (head === 'play_connect4' || head === 'c4' || head === 'connect4') {
        if (!rest) return { kind: 'client', op: { t: 'help' } };
        return { kind: 'c4_invite', to: rest.trim() };
    }
    // Connect 4 stats — `/c4stats [user]` (defaults to self)
    if (head === 'c4stats' || head === 'c4_stats') {
        const u = rest.trim();
        return u ? { kind: 'c4_stats', user: u } : { kind: 'c4_stats' };
    }

    // Text-style commands — transform body, broadcast styled say.
    // BUT: /rotate and /invert with NO body are the LEGACY screen-wide
    // toggles (chat_cmd Rotate_Chat / Invert_Color). They flip the entire
    // chat 180° / invert all colors until toggled again.
    if (STYLE_CMDS.has(head)) {
        if (!rest) {
            if (head === 'rotate') return { kind: 'cmd', c: 'Rotate_Chat' };
            if (head === 'invert') return { kind: 'cmd', c: 'Invert_Color' };
            return { kind: 'client', op: { t: 'help' } };
        }
        return { kind: 'styled', m: rest, style: head as 'rotate' | 'invert' | 'bbc' | 'body' };
    }

    // /font <#RRGGBB> <text>   → rotate-color-style with fixed hex
    if (head === 'font') {
        const m = /^(#[0-9A-Fa-f]{3,6}|red|green|blue|yellow|orange|purple|pink|white|black|gray)\s+(.+)$/i.exec(rest);
        if (!m) return { kind: 'client', op: { t: 'help' } };
        return { kind: 'styled', m: `[color=${m[1]}]${m[2]}[/color]`, style: 'bbc' };
    }

    // "/private" / "/siu" / etc. → chat_cmd broadcast
    if (CHAT_CMDS.has(head) || head === 'siu' || head === 'private') {
        return { kind: 'cmd', c: head };
    }

    // Unknown — surface as chat_cmd ("<user> did /xyz") to mirror the
    // legacy fallback behaviour of broadcasting unknown commands.
    return { kind: 'cmd', c: head };
}
